import type { ReactNode } from "react";

import { useState } from "react";

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@alaffia-technology-solutions/ds";

import {
  IconCircleMinus,
  IconCirclePlus,
  IconCopy,
  IconDotsVertical,
  IconPlus,
  IconUnlink,
} from "@tabler/icons-react";
import { Link } from "lucide-react";

interface PDFTextActionsLinkTarget {
  id: string;
  index: number;
  title: string;
  variant: "supporting" | "disqualifying";
}

interface PDFTextActionsProps {
  evidenceCardOpen?: boolean;
  linkTargets?: PDFTextActionsLinkTarget[];
  linked?: boolean | null;
  linkedEvidenceTitle?: string | null;
  onAddAnnotation?: () => unknown;
  onAddAsDisqualifying?: () => unknown;
  onAddAsSupporting?: () => unknown;
  onAddToEvidence?: () => unknown;
  onCopy?: () => unknown;
  onDelete?: () => unknown;
  onLink?: (target: PDFTextActionsLinkTarget) => unknown;
  onMore?: () => unknown;
  onSaveForLater?: () => unknown;
  onUnlink?: () => unknown;
  selection?: "new" | "old";
  showAddAnnotation?: boolean;
}

const ActionButton = ({
  children,
  destructive,
  disabled,
  icon: Icon,
  onClick,
}: {
  children: ReactNode;
  destructive?: boolean;
  disabled?: boolean;
  icon?: React.ComponentType<{ className?: string }>;
  onClick?: () => unknown;
}) => (
  <Button
    variant="ghost"
    size="xs"
    onClick={onClick}
    className={`af-gap-1 af-px-2 af-text-xs af-font-medium ${
      destructive
        ? "af-text-destructive hover:af-text-destructive"
        : "af-text-foreground hover:af-text-foreground"
    }`}
    disabled={disabled}
  >
    {Icon && <Icon className="af-size-3" />}
    {children}
  </Button>
);

export function PDFTextActions({
  evidenceCardOpen = false,
  linkTargets = [],
  linked = null,
  linkedEvidenceTitle = null,
  onAddAnnotation,
  onAddAsDisqualifying,
  onAddAsSupporting,
  onAddToEvidence,
  onCopy,
  onDelete,
  onLink,
  onMore,
  onSaveForLater,
  onUnlink,
  selection = "new",
  showAddAnnotation = true,
}: PDFTextActionsProps) {
  const [moreOpen, setMoreOpen] = useState(false);

  if (selection === "new" && onAddAnnotation) {
    return (
      <div className="af-flex af-items-center af-gap-1">
        {onCopy && (
          <ActionButton icon={IconCopy} onClick={onCopy}>
            Copy
          </ActionButton>
        )}
        {showAddAnnotation && onAddAnnotation && (
          <ActionButton icon={IconPlus} onClick={onAddAnnotation}>
            Add
          </ActionButton>
        )}
      </div>
    );
  }

  const isNewSelection = selection === "new";
  const isLinked = linked === true;
  const isUnlinkedOldSelection = selection === "old" && linked === false;
  const supportingLinkTargets = linkTargets.filter(
    (target) => target.variant === "supporting",
  );
  const disqualifyingLinkTargets = linkTargets.filter(
    (target) => target.variant === "disqualifying",
  );
  const hasLinkTargets = linkTargets.length > 0;
  const hasOverflowActions = Boolean(
    onCopy ?? (isNewSelection && onSaveForLater),
  );

  const linkDropdown = hasLinkTargets ? (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="xs"
          className="af-gap-1 af-px-2 af-text-xs af-font-medium af-text-foreground hover:af-text-foreground"
        >
          <Link className="af-size-3" />
          Link to...
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="af-w-56 af-rounded-md af-border-interface-border af-bg-popover af-p-1 af-shadow-[0px_4px_6px_rgba(42,100,246,0.07),0px_2px_4px_rgba(23,37,84,0.05)]"
      >
        {supportingLinkTargets.length > 0 && (
          <DropdownMenuGroup>
            <DropdownMenuLabel className="af-px-2 af-py-1.5 af-text-xs af-font-semibold">
              Supporting
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="af-my-1" />
            {supportingLinkTargets.map((target) => (
              <DropdownMenuItem
                key={target.id}
                className="af-px-2 af-py-1.5 af-text-xs"
                onSelect={() => onLink?.(target)}
              >
                <span className="af-truncate">{target.title}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
        )}
        {supportingLinkTargets.length > 0 &&
          disqualifyingLinkTargets.length > 0 && (
            <DropdownMenuSeparator className="af-my-1" />
          )}
        {disqualifyingLinkTargets.length > 0 && (
          <DropdownMenuGroup>
            <DropdownMenuLabel className="af-px-2 af-py-1.5 af-text-xs af-font-semibold">
              Disqualifying
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="af-my-1" />
            {disqualifyingLinkTargets.map((target) => (
              <DropdownMenuItem
                key={target.id}
                className="af-px-2 af-py-1.5 af-text-xs"
                onSelect={() => onLink?.(target)}
              >
                <span className="af-truncate">{target.title}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  ) : null;

  const unlinkLabel = linkedEvidenceTitle
    ? `Unlink from ${linkedEvidenceTitle}`
    : evidenceCardOpen
      ? "Unlink From This Evidence"
      : "Unlink";

  return (
    <div className="af-flex af-items-center af-gap-1">
      {evidenceCardOpen ? (
        isLinked ? (
          <ActionButton icon={IconUnlink} onClick={onUnlink}>
            {unlinkLabel}
          </ActionButton>
        ) : (
          <ActionButton icon={IconPlus} onClick={onAddToEvidence}>
            Add to This Evidence
          </ActionButton>
        )
      ) : (
        <>
          {(!isLinked || isNewSelection) && (
            <ActionButton icon={IconCirclePlus} onClick={onAddAsSupporting}>
              Add as Supporting
            </ActionButton>
          )}
          {(!isLinked || isNewSelection) && (
            <ActionButton icon={IconCircleMinus} onClick={onAddAsDisqualifying}>
              Add as Disqualifying
            </ActionButton>
          )}
          {isLinked ? (
            <ActionButton icon={IconUnlink} onClick={onUnlink}>
              {unlinkLabel}
            </ActionButton>
          ) : (
            linkDropdown
          )}
        </>
      )}

      {(selection === "old" || isUnlinkedOldSelection) && (
        <ActionButton destructive onClick={onDelete} disabled={true}>
          Delete
        </ActionButton>
      )}

      <div className="af-h-5 af-w-px af-bg-interface-border" />
      <div className="af-relative">
        {hasOverflowActions && moreOpen && (
          <div className="af-absolute af-right-0 af-top-7 af-z-50 af-w-32 af-rounded-md af-border af-border-interface-border af-bg-popover af-p-1 af-text-popover-foreground af-shadow-[0px_4px_6px_rgba(42,100,246,0.07),0px_2px_4px_rgba(23,37,84,0.05)]">
            {isNewSelection && onSaveForLater && (
              <Button
                type="button"
                disabled
                size="sm"
                variant="ghost"
                className="af-flex af-w-full af-items-center af-justify-start af-gap-2 af-rounded-sm af-px-2 af-py-1.5 af-text-left af-text-xs hover:af-bg-accent hover:af-text-accent-foreground"
                onClick={() => {
                  setMoreOpen(false);
                  onSaveForLater();
                }}
              >
                Save For Later
              </Button>
            )}
            {onCopy && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="af-flex af-w-full af-items-center af-justify-start af-gap-2 af-rounded-sm af-px-2 af-py-1.5 af-text-left af-text-xs hover:af-bg-accent hover:af-text-accent-foreground"
                onClick={() => {
                  setMoreOpen(false);
                  onCopy();
                }}
              >
                Copy Text
              </Button>
            )}
          </div>
        )}
        {hasOverflowActions ? (
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={() => {
              onMore?.();
              setMoreOpen((open) => !open);
            }}
          >
            <IconDotsVertical className="af-size-3" />
          </Button>
        ) : (
          <Button variant="ghost" size="icon-xs" onClick={onMore}>
            <IconDotsVertical className="af-size-3" />
          </Button>
        )}
      </div>
    </div>
  );
}
